// 選択した港/チョークポイントの詳細。指標ごとの最新値・異常度・前月比＋自前SVGスパークライン。
import { THRESHOLDS } from "../../scripts/lib/anomaly.js";
import { formatCompact, formatNumber, formatPct } from "../utils/format.js";
import { SIGNAL_LABEL } from "../utils/labels.js";

// 異常度（σ）は classify() と同じ閾値でシグナル色にする（判定の主役なので目立たせる）。
function sigmaClass(sigma) {
  const abs = Math.abs(sigma ?? 0);
  if (abs >= THRESHOLDS.danger.sigma) return "sigma-danger";
  if (abs >= THRESHOLDS.warn.sigma) return "sigma-warn";
  return "";
}

// 指標定義（港と要衝で列が異なる。tons 指標は「推計」注記）。
const METRIC_CONFIG = {
  port: [
    { key: "portcalls", label: "入港数", color: "var(--series-cyan)", tons: false },
    { key: "import", label: "輸入量", color: "var(--series-blue)", tons: true },
    { key: "export", label: "輸出量", color: "var(--series-purple)", tons: true },
  ],
  chokepoint: [
    { key: "n_total", label: "通航数", color: "var(--series-cyan)", tons: false },
    { key: "capacity", label: "通過貿易量", color: "var(--series-orange)", tons: true },
  ],
};

const SCOPE_LABEL = { chokepoint: "チョークポイント", port: "港" };

// 時系列を 100x30 のビューボックスへ正規化してポリラインで描く。
function Sparkline({ series, color }) {
  const points = (series ?? []).filter(
    (d) => d && d.value != null && Number.isFinite(d.value),
  );
  if (points.length < 2) return <div className="sparkline" />;

  const values = points.map((d) => d.value);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;
  const n = points.length;

  const coords = points
    .map((d, i) => {
      const x = (i / (n - 1)) * 100;
      const y = 28 - ((d.value - min) / span) * 26; // 上下 2px パディング
      return `${x.toFixed(2)},${y.toFixed(2)}`;
    })
    .join(" ");

  return (
    <svg
      className="sparkline"
      viewBox="0 0 100 30"
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      <polyline
        points={coords}
        fill="none"
        stroke={color}
        strokeWidth="1.4"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}

function PortDetail({
  location,
  onClose,
  onAnalyze,
  onShowNetwork,
  networkActive,
  networkLoading,
}) {
  if (!location) return null;

  // 波及ネットワークは港のみ対象（チョークポイントはデータが無い）。
  const canShowNetwork = onShowNetwork && location.kind === "port";

  const config = METRIC_CONFIG[location.kind] ?? METRIC_CONFIG.port;

  return (
    <section className="detail-popover">
      <div className="detail-head">
        <div>
          <h3 className="detail-title">{location.fullname || location.portname}</h3>
        </div>
        <button
          type="button"
          className="detail-close"
          onClick={onClose}
          aria-label="閉じる"
        >
          ×
        </button>
      </div>
      <p className="detail-sub">
        <span className={`badge signal-${location.signal}`}>
          <span className="dot" />
          {SIGNAL_LABEL[location.signal] ?? location.signal}
        </span>{" "}
        {SCOPE_LABEL[location.kind]}
        {location.country ? ` · ${location.country}` : ""}
      </p>

      {config.map((m) => {
        const stat = location.stats?.[m.key];
        const latest = stat?.latest ?? location.latest?.[m.key];
        const fmt = m.tons ? formatCompact : formatNumber;
        const pct = stat?.pctChange;

        return (
          <div className="metric-block" key={m.key}>
            <div className="metric-block-head">
              <span className="metric-block-name">
                {m.label}
                {m.tons ? "（推計t）" : ""}
              </span>
              <span className="metric-block-value" style={{ color: m.color }}>
                {fmt(latest)}
              </span>
            </div>
            <Sparkline series={location.metrics?.[m.key]} color={m.color} />
            {stat ? (
              <div className="metric-block-stats">
                <span
                  className={`metric-sigma ${sigmaClass(stat.sigma)}`}
                  title="過去1年の28日移動平均の分布に対する現在水準のZスコア（異常判定の主指標）"
                >
                  異常度 {stat.sigma}σ
                </span>
                <span
                  className="metric-sub"
                  title="直近28日平均と、その前の28日平均の変化率（補助指標）"
                >
                  前月比 {formatPct(pct)}
                </span>
              </div>
            ) : (
              <div className="metric-block-stats">
                <span>データ不足</span>
              </div>
            )}
          </div>
        );
      })}

      <p className="detail-note">
        数値は AIS 由来の推計値（最新 {location.latestDate ?? "—"}）。
      </p>

      {(location.pageid || canShowNetwork || onAnalyze) && (
        <div className="detail-actions">
          {location.pageid && (
            <a
              className="ghost-button detail-link"
              href={`https://portwatch.imf.org/pages/${location.pageid}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              PortWatch で詳細 ↗
            </a>
          )}
          {canShowNetwork && (
            <button
              type="button"
              className="ghost-button"
              onClick={() => onShowNetwork(location)}
              disabled={networkLoading}
            >
              {networkLoading
                ? "読込中…"
                : networkActive
                  ? "ネットワークを隠す"
                  : "ネットワークを表示"}
            </button>
          )}
          {onAnalyze && (
            <button
              type="button"
              className="ghost-button"
              onClick={() => onAnalyze(location)}
            >
              AI に分析させる
            </button>
          )}
        </div>
      )}
    </section>
  );
}

export default PortDetail;
